import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Eyebrow } from "@/components/ui-bits";

const API = process.env.REACT_APP_BACKEND_URL;
const CLAIM_TYPES = ["Warranty claim", "Delivery damage / shortage", "Return or cancellation", "Other"];

export default function Claims() {
  const [f, setF] = useState({
    contact_name: "", email: "", phone: "", company: "", product: "", serial: "",
    installer: "", purchase_date: "", claim_type: "Warranty claim", issue: "", consent: false, company_website: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) { toast.error("Please accept the terms to continue."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/claims`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Submission failed");
      const data = await res.json();
      setDone(data.reference);
      toast.success("Your claim has been submitted.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-sm border border-ei-mist px-3 py-2.5 text-ei-navy text-sm focus:border-ei-gold outline-none";

  return (
    <>
      <Seo title="Warranty & returns claims — EI Battery Solutions" description="Lodge a warranty, delivery or returns claim with EI Battery Solutions." />
      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Eyebrow className="text-ei-gold">Support</Eyebrow>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight">Lodge a claim</h1>
          <p className="mt-5 text-lg text-ei-white/75 max-w-2xl leading-relaxed">
            Use this form for warranty, delivery-damage and returns claims. Dyness provides the applicable
            manufacturer warranty for eligible Dyness products; EI Battery does not offer an additional voluntary
            supplier product warranty. We assist you and your installation contractor to lodge, document and follow
            up Dyness warranty matters, and this does not prevent any person exercising non-excludable rights.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {done ? (
          <div className="bg-white border border-ei-mist rounded-md p-8 text-center" data-testid="claim-success">
            <ShieldCheck className="w-10 h-10 text-ei-golddark mx-auto" />
            <h2 className="mt-4 font-display text-2xl font-bold text-ei-navy">Claim received</h2>
            <p className="mt-2 text-sm text-ei-graphite/80">Your reference is <strong>{done}</strong>. Our team will review and be in touch. A copy has been emailed to you.</p>
            <Link to="/" className="mt-6 inline-flex text-sm font-bold text-ei-navy hover:text-ei-golddark">Back to home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-ei-mist rounded-md p-6 sm:p-8 space-y-4" data-testid="claim-form">
            <input type="text" tabIndex={-1} autoComplete="off" value={f.company_website} onChange={set("company_website")} className="hidden" aria-hidden />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Your name" data-testid="claim-name" value={f.contact_name} onChange={set("contact_name")} className={input} />
              <input required type="email" placeholder="Email" data-testid="claim-email" value={f.email} onChange={set("email")} className={input} />
              <input required placeholder="Phone" data-testid="claim-phone" value={f.phone} onChange={set("phone")} className={input} />
              <input placeholder="Business (optional)" data-testid="claim-company" value={f.company} onChange={set("company")} className={input} />
            </div>
            <select data-testid="claim-type" value={f.claim_type} onChange={set("claim_type")} className={input}>
              {CLAIM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Product / model" data-testid="claim-product" value={f.product} onChange={set("product")} className={input} />
              <input placeholder="Serial number (optional)" data-testid="claim-serial" value={f.serial} onChange={set("serial")} className={input} />
              <input placeholder="Installing contractor (optional)" data-testid="claim-installer" value={f.installer} onChange={set("installer")} className={input} />
              <input placeholder="Purchase date (optional)" data-testid="claim-date" value={f.purchase_date} onChange={set("purchase_date")} className={input} />
            </div>
            <textarea required placeholder="Describe the issue — what happened, when, and any error codes or photos you can share by email." rows={5} data-testid="claim-issue" value={f.issue} onChange={set("issue")} className={input} />
            <label className="flex items-start gap-2 text-xs text-ei-graphite/70">
              <input type="checkbox" data-testid="claim-consent" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-0.5" />
              I confirm the above is accurate and agree to be contacted about this claim, per the <Link to="/policies/privacy" className="text-ei-golddark underline">privacy policy</Link>.
            </label>
            <button type="submit" disabled={loading} data-testid="claim-submit"
              className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-5 py-3.5 rounded-sm hover:bg-ei-ink transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Submit claim
            </button>
            <p className="text-xs text-ei-graphite/55 leading-relaxed">
              This does not limit rights that cannot be excluded under the Australian Consumer Law. See our
              {" "}<Link to="/policies/warranty" className="underline">warranty</Link> and <Link to="/policies/returns" className="underline">returns</Link> policies.
            </p>
          </form>
        )}
      </section>
    </>
  );
}
